// Shared resolver for turning an operational message into a safe, read-only
// navigation destination inside the app.
//
// This extracts the per-`target_type` navigation logic that already lived,
// duplicated nowhere else, in `useOpenOperationalMessages.ts` and powers
// `MessageCenter.tsx`'s "Go" button. Per Milestone 4 Finding 10
// (`docs/operational-intelligence-milestone-4-design.md`), the backend's
// `SAFE_ACTION_REGISTRY` (`weatheragent/main.py`) only ever proposes an
// intent-shaped action key (`open_device`, `open_agent_logs`, `open_event_map`,
// `show_readings`, `show_message_history`, `inspect_context`) — it never knows
// about frontend route shapes. This module is the one place that maps those
// intents (or, with no explicit key, a message's own `target_type`) onto
// concrete in-app paths, so a future caller (the Fix Window's Actions tab)
// can call `resolveOperationalMessageAction(message, relatedEntities, actionKey)`
// without first having to know or re-derive the message's `target_type`.

export type SafeOperationalActionKey =
  | "open_device"
  | "open_agent_logs"
  | "open_event_map"
  | "show_readings"
  | "show_message_history"
  | "inspect_context";

export type OperationalMessageActionTarget = {
  target_type?: string | null;
  target_id?: number | string | null;
  source_type?: string | null;
  source_id?: number | string | null;
};

export type MonitorActionLookupEntry = {
  inventory_device_id?: number | string | null;
};

export type LoadAssignmentActionLookupEntry = {
  id?: number | string | null;
  event_id?: number | string | null;
  virtual_device_id?: number | string | null;
};

export type VirtualDeviceActionLookupEntry = {
  id?: number | string | null;
  zone_id?: number | string | null;
};

export type AgentActionLookupEntry = {
  id?: number | string | null;
};

export type OperationalMessageActionRelatedEntities = {
  monitorsById?: Map<number, MonitorActionLookupEntry>;
  loadAssignmentsByMonitorId?: Map<number, LoadAssignmentActionLookupEntry>;
  virtualDeviceByInstanceId?: Map<number, VirtualDeviceActionLookupEntry>;
  eventIdByZoneId?: Map<number, number>;
  agentsById?: Map<number, AgentActionLookupEntry>;
};

export type ResolvedOperationalMessageAction = {
  path: string;
};

export const normalizeOperationalActionId = (
  value?: number | string | null
): number | null => {
  const numericValue = Number(value);
  return Number.isFinite(numericValue) ? numericValue : null;
};

type MonitorTargetResolution = {
  monitorId: number | null;
  parentDeviceId: number | null;
  eventId: number | null;
  eventDeviceId: number | null;
  loadAssignmentId: number | null;
};

type DeviceTargetResolution = {
  deviceId: number | null;
  eventId: number | null;
  eventDeviceId: number | null;
};

const resolveMonitorTarget = (
  message: OperationalMessageActionTarget,
  relatedEntities: OperationalMessageActionRelatedEntities
): MonitorTargetResolution => {
  const monitorId = normalizeOperationalActionId(message.target_id);
  const monitor = monitorId === null ? undefined : relatedEntities.monitorsById?.get(monitorId);
  const parentDeviceId = normalizeOperationalActionId(monitor?.inventory_device_id);
  const assignment =
    monitorId === null ? undefined : relatedEntities.loadAssignmentsByMonitorId?.get(monitorId);

  return {
    monitorId,
    parentDeviceId,
    eventId: normalizeOperationalActionId(assignment?.event_id),
    eventDeviceId: normalizeOperationalActionId(assignment?.virtual_device_id),
    loadAssignmentId: normalizeOperationalActionId(assignment?.id),
  };
};

const resolveDeviceTarget = (
  message: OperationalMessageActionTarget,
  relatedEntities: OperationalMessageActionRelatedEntities
): DeviceTargetResolution => {
  const deviceId = normalizeOperationalActionId(message.target_id);
  const virtualDevice =
    deviceId === null ? undefined : relatedEntities.virtualDeviceByInstanceId?.get(deviceId);
  const zoneId = normalizeOperationalActionId(virtualDevice?.zone_id);
  const eventId = zoneId === null ? null : relatedEntities.eventIdByZoneId?.get(zoneId) ?? null;

  return {
    deviceId,
    eventId,
    eventDeviceId: normalizeOperationalActionId(virtualDevice?.id),
  };
};

const resolveAgentTarget = (message: OperationalMessageActionTarget): number | null => {
  const targetId = normalizeOperationalActionId(message.target_id);
  return targetId ?? normalizeOperationalActionId(message.source_id);
};

const buildMonitorEventMapPath = (resolution: MonitorTargetResolution): string | undefined => {
  if (resolution.eventId === null || resolution.loadAssignmentId === null) {
    return undefined;
  }
  return `/event-map/${resolution.eventId}?loadAssignmentId=${resolution.loadAssignmentId}${
    resolution.eventDeviceId !== null ? `&eventDeviceId=${resolution.eventDeviceId}` : ""
  }`;
};

const buildMonitorDevicePath = (resolution: MonitorTargetResolution): string | undefined => {
  if (resolution.parentDeviceId === null) {
    return undefined;
  }
  return `/devices?deviceId=${resolution.parentDeviceId}${
    resolution.monitorId === null ? "" : `&monitorId=${resolution.monitorId}`
  }`;
};

const buildMonitorNavigationPath = (resolution: MonitorTargetResolution): string | undefined =>
  buildMonitorEventMapPath(resolution) ?? buildMonitorDevicePath(resolution);

const buildAgentNavigationPath = (agentId: number | null): string =>
  agentId === null ? "/agents" : `/agents?agentId=${agentId}`;

const buildDeviceEventMapPath = (resolution: DeviceTargetResolution): string | undefined => {
  if (resolution.eventId === null || resolution.eventDeviceId === null) {
    return undefined;
  }
  return `/event-map/${resolution.eventId}?eventDeviceId=${resolution.eventDeviceId}`;
};

const buildDevicePlainPath = (resolution: DeviceTargetResolution): string | undefined => {
  if (resolution.deviceId === null) {
    return undefined;
  }
  return `/devices?deviceId=${resolution.deviceId}`;
};

const buildDeviceNavigationPath = (resolution: DeviceTargetResolution): string | undefined =>
  buildDeviceEventMapPath(resolution) ?? buildDevicePlainPath(resolution);

// Resolves the same single "primary" navigation path per message that
// `useOpenOperationalMessages.ts` has always computed, based solely on the
// message's own `target_type`. This is the byte-identical replacement for
// that hook's inline branching, and remains what `MessageCenter.tsx`'s "Go"
// button renders.
const resolveDefaultOperationalMessageAction = (
  message: OperationalMessageActionTarget,
  relatedEntities: OperationalMessageActionRelatedEntities
): ResolvedOperationalMessageAction | undefined => {
  if (message.target_type === "monitor") {
    const path = buildMonitorNavigationPath(resolveMonitorTarget(message, relatedEntities));
    return path === undefined ? undefined : { path };
  }

  if (message.target_type === "agent") {
    return { path: buildAgentNavigationPath(resolveAgentTarget(message)) };
  }

  const path = buildDeviceNavigationPath(resolveDeviceTarget(message, relatedEntities));
  return path === undefined ? undefined : { path };
};

// Resolves the device a message is (directly, or via a monitor's parent
// device) associated with, independent of which action key asked for it.
// Agent-targeted messages carry no device relationship in the data available
// to this resolver today, so they resolve to `null`.
const resolveActionDeviceId = (
  message: OperationalMessageActionTarget,
  relatedEntities: OperationalMessageActionRelatedEntities
): number | null => {
  if (message.target_type === "monitor") {
    return resolveMonitorTarget(message, relatedEntities).parentDeviceId;
  }
  if (message.target_type === "agent") {
    return null;
  }
  return resolveDeviceTarget(message, relatedEntities).deviceId;
};

const resolveExplicitOperationalMessageAction = (
  actionKey: SafeOperationalActionKey,
  message: OperationalMessageActionTarget,
  relatedEntities: OperationalMessageActionRelatedEntities
): ResolvedOperationalMessageAction | undefined => {
  switch (actionKey) {
    case "open_device": {
      const deviceId = resolveActionDeviceId(message, relatedEntities);
      return deviceId === null ? undefined : { path: `/devices?deviceId=${deviceId}` };
    }

    case "show_readings": {
      // `Readings.tsx` (the `/readings` route) does not read any query
      // params at all today — confirmed by reading the component, it has no
      // `useSearchParams`/`useLocation` usage and its device/instance filter
      // is local component state only, seeded from nothing in the URL. There
      // is no supported way to deep-link into readings for one device or
      // monitor yet, so this falls back to the device page, same as
      // `open_device`. Revisit this mapping if `Readings.tsx` ever grows a
      // `deviceId`/`instanceId` query param.
      const deviceId = resolveActionDeviceId(message, relatedEntities);
      return deviceId === null ? undefined : { path: `/devices?deviceId=${deviceId}` };
    }

    case "open_agent_logs": {
      // There is no dedicated, working "Agent logs" route today:
      // `AgentLog.tsx` (the `/agent-log` route) is an unimplemented stub —
      // confirmed by reading it, it renders only static placeholder text and
      // ignores its route entirely. `AgentControl.tsx` (the `/agents` route)
      // does read `?agentId=` and uses it to select the matching Agent in
      // the Overview tab (see the `linkedAgentId` effect that reads
      // `searchParamNumber(searchParams, 'agentId')`), which is real,
      // working, already-shipped behavior — but it only selects the Agent,
      // it does not switch to a logs-specific view for it. Reusing
      // `/agents?agentId=` anyway since it is the only proven entry point;
      // this is the same path the default "Go" button already produces for
      // agent-targeted messages. Only agent-targeted messages carry an
      // agent id today, so other target types resolve to `undefined`.
      if (message.target_type !== "agent") {
        return undefined;
      }
      return { path: buildAgentNavigationPath(resolveAgentTarget(message)) };
    }

    case "open_event_map": {
      if (message.target_type === "monitor") {
        const path = buildMonitorEventMapPath(resolveMonitorTarget(message, relatedEntities));
        return path === undefined ? undefined : { path };
      }
      if (message.target_type === "agent") {
        return undefined;
      }
      const path = buildDeviceEventMapPath(resolveDeviceTarget(message, relatedEntities));
      return path === undefined ? undefined : { path };
    }

    case "show_message_history": {
      // `MessageCenter.tsx` (the `/messages` route) does not read any
      // query params either — confirmed by reading it, no
      // `useSearchParams` import — so there is no supported way to scroll
      // to or highlight one message yet. This always resolves to the
      // plain Message Center route.
      return { path: "/messages" };
    }

    case "inspect_context": {
      // `inspect_context` is not a navigation action — it refers to the
      // richer `context` object already returned by the troubleshoot route
      // and rendered in-modal (the Fix Window's "Evidence"/"Expert" areas
      // per the Milestone 4 design), not a page to route to. Deliberately
      // returns `undefined` here; a future caller (Slice 5's Actions tab)
      // should handle this key by switching tabs within the Fix Window
      // rather than by consuming a `path` from this resolver.
      return undefined;
    }

    default: {
      const exhaustiveCheck: never = actionKey;
      return exhaustiveCheck;
    }
  }
};

/**
 * Resolves a safe, read-only in-app navigation destination for an
 * operational message.
 *
 * - Called with no `actionKey` (or `undefined`), this reproduces exactly the
 *   single `navigationPath`-per-message logic `useOpenOperationalMessages.ts`
 *   has always computed from the message's own `target_type` — this is what
 *   `MessageCenter.tsx`'s "Go" button renders.
 * - Called with an explicit `actionKey` from the backend's
 *   `SAFE_ACTION_REGISTRY` (`weatheragent/main.py`), this resolves the path
 *   for that specific intent, independent of the message's `target_type`,
 *   so a caller does not need to already know what kind of message it has.
 */
export const resolveOperationalMessageAction = (
  message: OperationalMessageActionTarget,
  relatedEntities: OperationalMessageActionRelatedEntities = {},
  actionKey?: SafeOperationalActionKey
): ResolvedOperationalMessageAction | undefined => {
  if (!actionKey) {
    return resolveDefaultOperationalMessageAction(message, relatedEntities);
  }
  return resolveExplicitOperationalMessageAction(actionKey, message, relatedEntities);
};

export default resolveOperationalMessageAction;
